import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import axios from 'axios';
import { lightTheme, darkTheme } from '../theme';
import { getApiUrl } from '../utils/apiUrl';

interface ThemeContextType {
    isDarkMode: boolean;
    toggleTheme: () => void;
    setThemeMode: (mode: PaletteMode) => void;
}

const THEME_KEY = 'leadforge_theme_preference';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Simplified initialization - just check localStorage and user data once
    const [mode, setMode] = useState<PaletteMode>(() => {
        try {
            // Check user preference in sessionStorage first
            const userDataStr = sessionStorage.getItem('user');
            if (userDataStr) {
                const user = JSON.parse(userDataStr);
                if (user.themePreference) {
                    return user.themePreference as PaletteMode;
                }
            }
        } catch (error) {
            // Silent fallback
        }
        
        // Fallback to localStorage
        const savedTheme = localStorage.getItem(THEME_KEY);
        return (savedTheme as PaletteMode) || 'light';
    });

    // Simplified update function without debouncing
    const updateTheme = useCallback((newMode: PaletteMode) => {
        setMode(newMode);
        localStorage.setItem(THEME_KEY, newMode);

        // Update server in background (fire and forget)
        const updateServer = async () => {
            const token = sessionStorage.getItem('token');
            if (!token) return;

            try {
                const user = JSON.parse(sessionStorage.getItem('user') || '{}');
                const isActualAdmin = user.role === 'admin' && !user.companyName;
                const endpoint = isActualAdmin 
                    ? `${getApiUrl()}/api/auth/theme-preference`
                    : `${getApiUrl()}/api/auth/client/theme-preference`;

                await axios.post(
                    endpoint,
                    { theme: newMode },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                // Update user data in sessionStorage without token replacement
                const updatedUser = { ...user, themePreference: newMode };
                sessionStorage.setItem('user', JSON.stringify(updatedUser));
            } catch (error) {
                // Silent fail - theme still works locally
            }
        };

        // Don't wait for server update
        updateServer();
    }, []);

    const colorMode = useMemo(
        () => ({
            isDarkMode: mode === 'dark',
            toggleTheme: () => {
                const newMode = mode === 'light' ? 'dark' : 'light';
                updateTheme(newMode);
            },
            setThemeMode: updateTheme
        }),
        [mode, updateTheme]
    );

    const theme = useMemo(
        () => mode === 'light' ? lightTheme : darkTheme,
        [mode]
    );



    return (
        <ThemeContext.Provider value={colorMode}>
            <MuiThemeProvider theme={theme}>
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
}; 